import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { dayTitle, pickLocation } from './visitUtils';

/**
 * Своя площадка: сменить день или саму площадку.
 *
 * Только для площадок, которые человек добавил сам, — назначенные правит офис.
 * Внешне — тот же диалог, что «Add a location»: день и площадка в тех же полях,
 * чтобы добавлять и исправлять было одним и тем же движением.
 */
const VisitEditDialog = ({ open, assignment, days, locations, onClose, onSave }) => {
    const [workDate, setWorkDate] = useState('');
    const [location, setLocation] = useState(null);

    useEffect(() => {
        if (open && assignment) {
            setWorkDate(assignment.workDate);
            setLocation(assignment.location || '');
        }
    }, [open, assignment]);

    const picked = useMemo(() => pickLocation(location, locations), [location, locations]);

    // День визита мог оказаться вне списка (например, неделю назад) — всё равно
    // показываем его, иначе выпадашка была бы пустой.
    const dayOptions = useMemo(() => {
        if (!assignment || days.some((d) => d.value === assignment.workDate)) return days;
        return [{ value: assignment.workDate, label: dayTitle(assignment.workDate) }, ...days];
    }, [days, assignment]);

    const changed = assignment
        && (workDate !== assignment.workDate || picked.label.toLowerCase() !== (assignment.location || '').trim().toLowerCase());

    const save = () => {
        if (!picked.label) return;
        onSave({ workDate, location: picked.label, jobCodes: picked.jobCodes });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Change this site</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    A site you added yourself. Sites the office scheduled for you are changed in the office.
                </Typography>

                <TextField
                    select
                    fullWidth
                    size="small"
                    label="Day"
                    value={workDate}
                    onChange={(e) => setWorkDate(e.target.value)}
                    sx={{ mb: 2 }}
                >
                    {dayOptions.map((d) => (
                        <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                    ))}
                </TextField>

                <Autocomplete
                    freeSolo
                    options={locations}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
                    value={location}
                    onChange={(e, v) => setLocation(v)}
                    onInputChange={(e, v, reason) => { if (reason === 'input') setLocation(v); }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            label="Location"
                            placeholder="Start typing to find a job site"
                        />
                    )}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={save}
                    disabled={!picked.label || !changed}
                    sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default VisitEditDialog;
